import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalculateOutlined as CalculateOutlinedIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

// import { db } from '../../../firebase/config';

// Firebase imports with proper types
// import { collection, getDocs, query, where } from '../../../firebase/firebase-mock';

import indexedDBService from '../../../utils/indexedDBService';

// Interface for firebase doc data
interface FirebaseDocData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  gstin?: string;
  userId?: string;
  createdAt?: string;
  description?: string;
  rate?: number;
  sellingPrice?: number;
  price?: number;
  hsnCode?: string;
  hsn?: string;
  gstRate?: number;
  taxRate?: number;
  category?: string;
  unit?: string;
  stockQuantity?: number;
  stock?: number;
  quantity?: number;
  productName?: string;
  details?: string;
  [key: string]: any;
}

// Interface for firebase query result
interface FirebaseQueryResult {
  docs: Array<{
    id: string;
    data: () => FirebaseDocData;
  }>;
  size: number;
  empty?: boolean;
}

// Interface for invoice data from parent component
interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';

  // Client information
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };

  // Items
  items: InvoiceItem[];

  // Totals
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;

  // Additional information
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;

  // Metadata
  userId: string;
  createdAt: string;
}

// Interface for invoice item from parent component
interface InvoiceItem {
  id: number; // Notice this is a number in CreateInvoiceDialog
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number;
}

// Interface for inventory item
interface InventoryItem {
  id: string;
  name: string;
  description: string;
  price: number;
  hsnCode?: string;
  gstRate?: number;
}

interface ItemsStepProps {
  invoiceData: InvoiceData;
  updateInvoiceData: (data: Partial<InvoiceData>) => void;
  userId?: string;
  inventory?: any[];
}

// Sample product/service templates for quick addition
const ITEM_TEMPLATES = [
  { id: 1, name: 'Web Development', description: 'Web Development Services', rate: 75, taxable: true },
  { id: 2, name: 'UI/UX Design', description: 'UI/UX Design', rate: 60, taxable: true },
  { id: 3, name: 'Digital Marketing', description: 'Digital Marketing Services', rate: 50, taxable: true },
  { id: 4, name: 'Maintenance', description: 'Monthly Maintenance', rate: 100, taxable: true },
  { id: 5, name: 'Content Writing', description: 'Content Writing', rate: 40, taxable: true },
];

const ItemsStep: React.FC<ItemsStepProps> = ({
  invoiceData,
  updateInvoiceData,
  userId = '',
  inventory = []
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<InvoiceItem | null>(null);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>(inventory);
  const [loading, setLoading] = useState(false);

  // Form state for item dialog
  const [itemForm, setItemForm] = useState<{
    id: number;
    name: string;
    description: string;
    quantity: number;
    rate: number;
    inventoryItemId?: string;
    hsnCode?: string;
    gstRate?: number;
  }>({
    id: 0,
    name: '',
    description: '',
    quantity: 1,
    rate: 0,
    inventoryItemId: '',
    hsnCode: '',
    gstRate: 0,
  });

  // State for calculations
  const [subtotal, setSubtotal] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [total, setTotal] = useState(0);

  // Initialize line items from invoice data or with an empty array with one item
  const [items, setItems] = useState<InvoiceItem[]>(
    invoiceData.items && invoiceData.items.length > 0
      ? invoiceData.items
      : [{
        id: 1,
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      }]
  );

  // States for tax and discount settings
  const [taxType, setTaxType] = useState(invoiceData.taxType || 'percentage');
  const [taxRate, setTaxRate] = useState(invoiceData.taxRate || 18); // Default GST rate in India
  const [discountType, setDiscountType] = useState(invoiceData.discountType || 'percentage');
  const [discountValue, setDiscountValue] = useState(invoiceData.discountValue || 0);
  const [isTaxable, setIsTaxable] = useState<boolean>(invoiceData.taxable || true);
  const [showTemplates, setShowTemplates] = useState(false);

  // Fetch inventory items on component mount
  useEffect(() => {
    if (inventory && inventory.length > 0) {
      setInventoryItems(inventory);
      return;
    }

    const fetchInventoryItems = async () => {
      setLoading(true);
      try {
        const inventoryItems: any[] = await indexedDBService.getAllItems('inventory');
        let userInventory = userId ? inventoryItems.filter(i => i.userId === userId) : inventoryItems;

        // If no items found in inventory, try to fallback to other collections logic by just returning empty for now
        // or check mocked data if we had it.

        // Transform the data
        const items = userInventory.map(data => {
          return {
            id: data.id,
            name: data.name || 'Unnamed Item',
            description: data.description || '',
            price: data.rate || data.sellingPrice || data.price || 0,
            hsnCode: data.hsnCode || '',
            gstRate: data.gstRate || 18,
            category: data.category || 'Uncategorized',
            unit: data.unit || 'Nos',
            stock: data.stockQuantity || data.stock || 0
          };
        });

        setInventoryItems(items);

      } catch (error) {
        console.error('Error fetching inventory items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventoryItems();
  }, [userId, inventory]);

  // Calculate totals whenever invoice data changes
  useEffect(() => {
    calculateTotals();
  }, [items, taxRate, discountType, discountValue, isTaxable]);

  // Handle item selection from inventory
  const handleItemSelect = (inventoryItemId: string) => {
    const selectedItem = inventoryItems.find(item => item.id === inventoryItemId);
    if (selectedItem) {
      setItemForm({
        ...itemForm,
        inventoryItemId: selectedItem.id,
        name: selectedItem.name,
        description: selectedItem.description || '',
        rate: selectedItem.price,
        hsnCode: selectedItem.hsnCode || '',
        gstRate: selectedItem.gstRate || 0,
      });
    }
  };

  // Calculate amount from quantity and rate
  const calculateAmount = (quantity: number, rate: number) => {
    return quantity * rate;
  };

  // Open dialog to add a new item
  const handleAddItem = () => {
    setEditingItem(null);
    setItemForm({
      id: Date.now(),
      name: '',
      description: '',
      quantity: 1,
      rate: 0,
      inventoryItemId: '',
      hsnCode: '',
      gstRate: 0,
    });
    setOpenDialog(true);
  };

  // Open dialog to edit an existing item
  const handleEditItem = (item: InvoiceItem) => {
    setEditingItem(item);
    setItemForm({
      id: item.id,
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      inventoryItemId: item.inventoryItemId || '',
      hsnCode: item.hsnCode || '',
      gstRate: item.gstRate || 0,
    });
    setOpenDialog(true);
  };

  // Delete an item from the list
  const handleDeleteItem = (itemId: number) => {
    setItems(items.filter(item => item.id !== itemId));
    calculateTotals();
  };

  // Handle changes to the item form
  const handleInputChange = (field: string, value: string | number) => {
    setItemForm({ ...itemForm, [field]: value });
  };

  // Save the current item (add or update)
  const handleSaveItem = () => {
    const amount = calculateAmount(itemForm.quantity, itemForm.rate);

    const newItem: InvoiceItem = {
      id: itemForm.id,
      name: itemForm.name,
      description: itemForm.description,
      quantity: itemForm.quantity,
      rate: itemForm.rate,
      amount,
      inventoryItemId: itemForm.inventoryItemId,
      hsnCode: itemForm.hsnCode,
      gstRate: itemForm.gstRate,
    };

    if (editingItem) {
      // Update existing item
      setItems(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      // Add new item
      setItems([...items, newItem]);
    }

    setOpenDialog(false);
    calculateTotals();
  };

  // Calculate subtotal, tax, discount, and total
  const calculateTotals = () => {
    // Calculate subtotal
    const newSubtotal = items.reduce((acc, item) => acc + item.amount, 0);

    // Calculate discount
    let newDiscountAmount = 0;
    if (discountType === 'percentage') {
      newDiscountAmount = (newSubtotal * discountValue) / 100;
    } else {
      newDiscountAmount = discountValue;
    }

    // Calculate tax - only if GSTIN is provided
    let newTaxAmount = 0;
    const hasGstin = invoiceData.clientDetails.gstin && invoiceData.clientDetails.gstin.trim() !== '';
    if (isTaxable && hasGstin) {
      const taxableAmount = newSubtotal - newDiscountAmount;
      newTaxAmount = (taxableAmount * taxRate) / 100;
    }

    // Calculate total
    const newTotal = newSubtotal - newDiscountAmount + newTaxAmount;

    // Update state
    setSubtotal(newSubtotal);
    setDiscountAmount(newDiscountAmount);
    setTaxAmount(newTaxAmount);
    setTotal(newTotal);

    // Update invoice data
    updateInvoiceData({
      items,
      subtotal: newSubtotal,
      discount: newDiscountAmount,
      tax: newTaxAmount,
      amount: newTotal,
      taxRate,
      taxType,
      discountType,
      discountValue,
      taxable: Boolean(isTaxable && hasGstin), // Cast to boolean to ensure it's not an empty string
    });
  };

  // Handle tax rate change
  const handleTaxRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaxRate(Number(e.target.value));
  };

  // Handle tax type change
  const handleTaxTypeChange = (e: SelectChangeEvent<string>) => {
    setTaxType(e.target.value);
  };

  // Handle discount type change
  const handleDiscountTypeChange = (e: SelectChangeEvent<string>) => {
    setDiscountType(e.target.value as 'percentage' | 'fixed');
  };

  // Handle discount value change
  const handleDiscountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountValue(Number(e.target.value));
  };

  // Handle taxable change
  const handleTaxableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTaxable(Boolean(e.target.checked));
  };

  // Format currency amounts
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Add a new blank line item
  const addItem = () => {
    setItems([
      ...items,
      {
        id: items.length + 1,
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      }
    ]);
  };

  // Add an item from template
  const addTemplateItem = (template: typeof ITEM_TEMPLATES[0]) => {
    setItems([
      ...items,
      {
        id: items.length + 1,
        name: template.name,
        description: template.description,
        quantity: 1,
        rate: template.rate,
        amount: template.rate, // quantity is 1, so amount = rate
      }
    ]);
    setShowTemplates(false);
  };

  // Remove an item at specified index
  const removeItem = (index: number) => {
    if (items.length === 1) {
      // Don't remove the last item, just clear it
      setItems([{ id: 1, name: '', description: '', quantity: 1, rate: 0, amount: 0 }]);
    } else {
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
    }
    calculateTotals();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Invoice Items</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          size="small"
        >
          Add Item
        </Button>
      </Box>

      {items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No items added yet. Click "Add Item" to add items to this invoice.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Item</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.description || '-'}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditItem(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => removeItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Buttons for adding items */}
      <Box sx={{ display: 'flex', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addItem}
          sx={{ mr: 2 }}
        >
          Add Item
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowTemplates(!showTemplates)}
        >
          Use Template
        </Button>
      </Box>

      {/* Templates section */}
      {showTemplates && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Item Templates
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {ITEM_TEMPLATES.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => addTemplateItem(template)}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {template.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rate: {formatCurrency(template.rate)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Tax & Discount Section */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Tax & Discount
        </Typography>

        <Grid container spacing={3}>
          {/* Tax Configuration */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tax Settings
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Tax Type</InputLabel>
                <Select
                  value={taxType}
                  label="Tax Type"
                  onChange={handleTaxTypeChange}
                >
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={taxType === 'percentage' ? 'Tax Rate (%)' : 'Tax Amount'}
                type="number"
                size="small"
                value={taxRate}
                onChange={handleTaxRateChange}
                InputProps={taxType === 'fixed' ? {
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                } : undefined}
              />
            </Box>
          </Grid>

          {/* Discount Configuration */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Discount Settings
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={discountType}
                  label="Discount Type"
                  onChange={handleDiscountTypeChange}
                >
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount'}
                type="number"
                size="small"
                value={discountValue}
                onChange={handleDiscountValueChange}
                InputProps={discountType === 'fixed' ? {
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                } : undefined}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Summary */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '250px', mb: 1 }}>
            <Typography>Subtotal:</Typography>
            <Typography>₹{subtotal.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '250px', mb: 1 }}>
            <Typography>
              Tax ({taxType === 'percentage' ? `${taxRate}%` : 'Fixed'}):
              {taxType === 'percentage' && (
                <Tooltip title="Applied only to taxable items">
                  <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', fontSize: '16px' }} />
                </Tooltip>
              )}
            </Typography>
            <Typography>₹{taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '250px', mb: 1 }}>
            <Typography>
              Discount ({discountType === 'percentage' ? `${discountValue}%` : 'Fixed'}):
            </Typography>
            <Typography>₹{discountAmount.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '250px', mb: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Typography fontWeight="bold">Total:</Typography>
            <Typography fontWeight="bold">₹{total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Item Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Item' : 'Add Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select from Inventory (Optional)"
                value={itemForm.inventoryItemId || ''}
                onChange={(e) => handleItemSelect(e.target.value)}
                size="small"
              >
                <MenuItem value="">- Custom Item -</MenuItem>
                {inventoryItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} - {formatCurrency(item.price)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Item Name"
                value={itemForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HSN/SAC Code (Optional)"
                value={itemForm.hsnCode || ''}
                onChange={(e) => handleInputChange('hsnCode', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={itemForm.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                label="Quantity"
                type="number"
                InputProps={{ inputProps: { min: 1 } }}
                value={itemForm.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || 0)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                label="Rate"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  inputProps: { min: 0 }
                }}
                value={itemForm.rate}
                onChange={(e) => handleInputChange('rate', parseFloat(e.target.value) || 0)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="GST Rate % (Optional)"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100 }
                }}
                value={itemForm.gstRate || ''}
                onChange={(e) => handleInputChange('gstRate', parseFloat(e.target.value) || 0)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid',
                borderColor: 'divider',
                pt: 2
              }}>
                <Typography variant="subtitle1">
                  Amount: {formatCurrency(calculateAmount(itemForm.quantity, itemForm.rate))}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveItem}
            variant="contained"
            disabled={!itemForm.name || itemForm.quantity <= 0 || itemForm.rate <= 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemsStep; 